export const SALARY_STRUCTURES = {
  BELOW_30K: {
    id: "below_30k",
    threshold: 30000,
    components: [
      { id: "basic", name: "Basic", type: "fixed", value: 11632 },
      { id: "da", name: "DA", type: "fixed", value: 3614 },
      {
        id: "hra",
        name: "HRA",
        type: "percentage",
        value: 5,
        base: ["basic", "da"],
      },
      { id: "conveyance", name: "Conveyance", type: "fixed", value: 1600 },
      { id: "bonus", name: "Bonus", type: "fixed", value: 583 },
    ],
  },
  ABOVE_30K: {
    id: "above_30k",
    threshold: 30000,
    components: [
      {
        id: "basic_da",
        name: "Basic + DA",
        type: "percentage",
        value: 50,
        base: ["ctc"],
      },
      {
        id: "hra",
        name: "HRA",
        type: "percentage",
        value: 50,
        base: ["basic_da"],
      },
      { id: "conveyance", name: "Conveyance", type: "fixed", value: 1600 },
    ],
  },
};

export const STATUTORY_RULES = {
  PF: {
    ceiling: 15000,
    employee_rate: 0.12,
    employer_rate: 0.13,
  },
  ESIC: {
    limit: 21000,
    employee_rate: 0.0075,
    employer_rate: 0.0325,
  },
  GRATUITY: {
    rate: 0.0481,
    base: ["basic", "da", "lta", "basic_da"],
  },
  PT: [
    { gender: "Male", threshold: 0, standard: 200, february: 300 },
    { gender: "Female", threshold: 25000, standard: 200, february: 300 },
  ],
};

export const calculateDynamicSalary = (annualCTC, config, options = {}) => {
  if (!config) return null;

  const monthlyCTC = annualCTC / 12;
  const isBelow30k = monthlyCTC < 30000;
  const structureId = isBelow30k ? "below_30k" : "above_30k";

  const components = (config.salary_components || []).filter(
    (c) => c.structure_id === structureId,
  );
  const statutory = config.statutory_settings || STATUTORY_RULES;
  const ptRules = config.professional_tax_rules || [];

  const results = {
    monthlyCTC,
    annualCTC,
    earnings: {},
    deductions: {},
    contributions: {},
    summary: {
      gross: 0,
      totalDeductions: 0,
      net: 0,
      employerContributions: 0,
    },
  };

  const isFeb = new Date().getMonth() === 1;

  // 1. Calculate non-balancing earnings first
  const balancingComp = components.find((c) => c.calc === "balancing");
  const otherEarnings = components.filter((c) => c.calc !== "balancing");

  // Process earnings. We assume a single pass is enough or ctc-based.
  // In a real engine we might need to resolve dependencies properly.
  otherEarnings.forEach((comp) => {
    let val = 0;
    if (comp.calc === "fixed") {
      val = parseFloat(comp.value) || 0;
    } else if (comp.calc === "percentage") {
      let baseVal = 0;
      if (comp.base.includes("ctc")) {
        baseVal = monthlyCTC;
      } else {
        comp.base.forEach((code) => {
          baseVal += results.earnings[code] || 0;
        });
      }
      val = baseVal * (parseFloat(comp.value) / 100);
    }
    results.earnings[comp.code] = val;
  });

  // Calculate preliminary gross
  let preliminaryGross = Object.values(results.earnings).reduce(
    (a, b) => a + b,
    0,
  );

  // 2. Statutory Calculations
  // PF
  if (options.pf_enabled) {
    const basic =
      results.earnings["basic"] || results.earnings["basic_da"] || 0;
    const da = results.earnings["da"] || 0;
    const pfBase = Math.min(basic + da, parseFloat(statutory.pf_limit));
    results.deductions["pf_employee"] = pfBase * 0.12;
    results.contributions["pf_employer"] =
      pfBase * (parseFloat(statutory.pf_employer_rate) || 0.13);
  } else {
    results.deductions["pf_employee"] = 0;
    results.contributions["pf_employer"] = 0;
  }

  // Gratuity (using hardcoded rate if not in config)
  const gratuityBase =
    (results.earnings["basic"] || 0) +
    (results.earnings["da"] || 0) +
    (results.earnings["basic_da"] || 0);
  results.contributions["gratuity"] = gratuityBase * 0.0481;

  // Balancing logic to find final gross and ESI
  // monthlyCTC = Gross + Employer PF + Employer ESIC + Gratuity
  // monthlyCTC = (PreliminaryGross + Balancing) + PF_Employer + Gratuity + ESIC_Employer

  let balancingValue = 0;
  let finalGross = preliminaryGross;
  let esiEmployer = 0;
  let esiEmployee = 0;

  const pfExp = results.contributions["pf_employer"] || 0;
  const gratuityVal = results.contributions["gratuity"] || 0;

  if (options.esi_enabled) {
    // Gross + ESIC_Employer = monthlyCTC - pfExp - gratuityVal
    const budgetForGrossAndEsic = monthlyCTC - pfExp - gratuityVal;

    if (budgetForGrossAndEsic < parseFloat(statutory.esic_limit)) {
      // ESIC_Employer = Gross * Rate
      // budget = Gross + Gross * Rate = Gross * (1 + Rate)
      finalGross =
        budgetForGrossAndEsic / (1 + parseFloat(statutory.esic_employer_rate));
      esiEmployer = finalGross * parseFloat(statutory.esic_employer_rate);
      esiEmployee = finalGross * 0.0075;
      balancingValue = finalGross - preliminaryGross;
    } else {
      balancingValue = budgetForGrossAndEsic - preliminaryGross;
      finalGross = preliminaryGross + balancingValue;
    }
  } else {
    balancingValue = monthlyCTC - pfExp - gratuityVal - preliminaryGross;
    finalGross = preliminaryGross + balancingValue;
  }

  if (balancingComp) {
    results.earnings[balancingComp.code] = Math.max(0, balancingValue);
  }

  results.deductions["esi_employee"] = esiEmployee;
  results.contributions["esi_employer"] = esiEmployer;

  // PT
  const ptRule = ptRules.find((r) => r.gender === options.gender) || ptRules[0];
  if (ptRule && finalGross > parseFloat(ptRule.threshold)) {
    results.deductions["professional_tax"] = isFeb
      ? parseFloat(ptRule.february_amount)
      : parseFloat(ptRule.default_amount);
  } else {
    results.deductions["professional_tax"] = 0;
  }

  // Income Tax (Keep simple legacy it logic if needed, or set to 0)
  if (annualCTC > 1200000) {
    results.deductions["income_tax"] = (annualCTC * 0.1) / 12;
  } else {
    results.deductions["income_tax"] = 0;
  }

  // Summary
  results.summary.gross = finalGross;
  results.summary.employerContributions = pfExp + esiEmployer + gratuityVal;
  results.summary.totalDeductions = Object.values(results.deductions).reduce(
    (a, b) => a + b,
    0,
  );
  results.summary.net = results.summary.gross - results.summary.totalDeductions;

  return results;
};

export const calculateSalarySummary = (
  annualCTC,
  options = {},
  customComponents = [],
) => {
  const monthlyCTC = annualCTC / 12;
  const isBelow30k = monthlyCTC < SALARY_STRUCTURES.BELOW_30K.threshold;

  let result = {
    basic: 0,
    da: 0,
    hra: 0,
    conveyance: 0,
    bonus: 0,
    special: 0,
    lta: options.lta || 0,
    pf_employee: 0,
    pf_employer: 0,
    esi_employee: 0,
    esi_employer: 0,
    gratuity: 0,
    pt: 0,
    it: 0,
    custom: {},
  };

  const isFeb = new Date().getMonth() === 1;

  if (isBelow30k) {
    // Logic for Below 30k
    result.basic = 11632;
    result.da = 3614;
    result.hra = (result.basic + result.da) * 0.05;
    result.conveyance = 1600;
    result.bonus = 583;

    // PF Calculation
    const pfBase = result.basic + result.da + result.conveyance + result.bonus;
    if (options.pf_enabled) {
      if (pfBase > STATUTORY_RULES.PF.ceiling) {
        result.pf_employee = 1800;
        result.pf_employer = 1950;
      } else {
        result.pf_employee = pfBase * STATUTORY_RULES.PF.employee_rate;
        result.pf_employer = pfBase * STATUTORY_RULES.PF.employer_rate;
      }
    }

    result.gratuity =
      (result.basic + result.da) * STATUTORY_RULES.GRATUITY.rate;

    const targetForGrossAndEsic =
      monthlyCTC - result.pf_employer - result.gratuity;
    let grossBudget = targetForGrossAndEsic;

    if (
      options.esi_enabled &&
      targetForGrossAndEsic < STATUTORY_RULES.ESIC.limit
    ) {
      grossBudget =
        targetForGrossAndEsic / (1 + STATUTORY_RULES.ESIC.employer_rate);
      result.esi_employer = grossBudget * STATUTORY_RULES.ESIC.employer_rate;
      result.esi_employee = grossBudget * STATUTORY_RULES.ESIC.employee_rate;
    }

    const sumFixed =
      result.basic + result.da + result.hra + result.conveyance + result.bonus;
    if (sumFixed > grossBudget) {
      const scale = grossBudget / sumFixed;
      result.basic *= scale;
      result.da *= scale;
      result.hra = (result.basic + result.da) * 0.05;
      result.conveyance *= scale;
      result.bonus *= scale;
      result.gratuity =
        (result.basic + result.da) * STATUTORY_RULES.GRATUITY.rate;
      result.special = 0;
    } else {
      result.special = grossBudget - sumFixed;
    }

    // PT
    const grossTotal =
      result.basic +
      result.da +
      result.hra +
      result.conveyance +
      result.bonus +
      result.special;
    const ptRule =
      STATUTORY_RULES.PT.find((r) => r.gender === options.gender) ||
      STATUTORY_RULES.PT[0];
    if (grossTotal > ptRule.threshold) {
      result.pt = isFeb ? ptRule.february : ptRule.standard;
    }
  } else {
    // Logic for Above 30k
    const basicDaSum = monthlyCTC * 0.5;
    result.basic = basicDaSum;
    result.da = 0;
    result.hra = basicDaSum * 0.5;
    result.conveyance = 1600;
    result.gratuity = (basicDaSum + result.lta) * STATUTORY_RULES.GRATUITY.rate;

    if (options.pf_enabled) {
      result.pf_employee = 1800;
      result.pf_employer = 1950;
    }

    const ptRule =
      STATUTORY_RULES.PT.find((r) => r.gender === options.gender) ||
      STATUTORY_RULES.PT[0];
    result.pt = isFeb ? ptRule.february : ptRule.standard;

    if (annualCTC > 1200000) {
      result.it = (annualCTC * 0.1) / 12;
    }

    result.special =
      monthlyCTC -
      (result.basic +
        result.da +
        result.hra +
        result.conveyance +
        result.bonus +
        result.lta +
        result.pf_employer +
        result.gratuity);
  }

  return result;
};
